import { InteractiveContainer } from "@jugl-web/ui-components";
import React, { useCallback, useState } from "react";
import { useTranslations } from "@jugl-web/utils";
import { LoginMethod } from "@web-src/modules/auth/pages/Login/types";
import { useVerifyOtpMutation } from "@web-src/features/auth/authApi";
import {
  PhoneInputValue,
  OTPVerificationForm,
} from "@jugl-web/ui-components/cross-platform";
import isObject from "lodash/isObject";

interface CXOtpStepProps {
  otpRequestId: string;
  loginMethod: LoginMethod;
  email: string;
  phone: PhoneInputValue;
  onVerified: (credentials: {
    token: string;
    refreshToken: string;
    login: string;
  }) => void;
  onBack: () => void;
  onResendOtp: (e?: React.FormEvent) => void;
}

export const CXOtpStep: React.FC<CXOtpStepProps> = ({
  otpRequestId,
  loginMethod,
  email,
  phone,
  onVerified,
  onBack,
  onResendOtp,
}) => {
  const { t } = useTranslations();

  const [verifyOtpMutation] = useVerifyOtpMutation();
  const [isLoading, setIsLoading] = useState(false);
  const [otpError, setOtpError] = useState("");

  const verifyOtp = useCallback(
    async (otp: string) => {
      if (isLoading) return;
      setOtpError("");
      setIsLoading(true);

      const resp = await verifyOtpMutation({ id: otpRequestId, otp });

      if ("data" in resp) {
        const { token, refreshToken } = resp.data;
        const login = phone.isValid ? `${phone.code} ${phone.phone}` : email;
        onVerified({ token, refreshToken, login });
      } else if (
        "data" in resp.error &&
        isObject(resp.error.data) &&
        "errors" in resp.error.data &&
        typeof resp.error.data.errors === "string"
      ) {
        setOtpError(resp.error.data.errors);
      }
      setIsLoading(false);
    },
    [email, isLoading, onVerified, otpRequestId, phone, verifyOtpMutation]
  );

  return (
    <div className="my-auto flex w-[500px] flex-col items-center justify-center py-4">
      <OTPVerificationForm
        title={
          loginMethod === LoginMethod.email
            ? t({
                id: "login-page.enter-code-sent-to-email",
                defaultMessage: "Enter Code Sent to Your Email",
              })
            : t({
                id: "login-page.enter-code-sent-to-number",
                defaultMessage: "Enter Code Sent to Your Number",
              })
        }
        subTitle={
          <div className="flex flex-col items-center gap-[6px]">
            <span className="text-grey-800 text-sm">
              {loginMethod === LoginMethod.email
                ? t(
                    {
                      id: "login-page.we-sent-it-to-email",
                      defaultMessage: "We sent it to the Email {email}",
                    },
                    {
                      email: <span className="text-primary-800">{email}</span>,
                    }
                  )
                : t(
                    {
                      id: "login-page.we-sent-it-to-number",
                      defaultMessage: "We sent it to the Phone Number {number}",
                    },
                    {
                      number: (
                        <span className="text-primary font-medium">{`${phone.code} ${phone.phone}`}</span>
                      ),
                    }
                  )}
            </span>
            <InteractiveContainer
              className="text-grey-900 hover:text-primary-800 w-max px-3 text-sm underline transition-all duration-300 hover:font-medium"
              onClick={onBack}
            >
              {loginMethod === LoginMethod.email
                ? t({
                    id: "login-page.change-email",
                    defaultMessage: "Change Email Address",
                  })
                : t({
                    id: "login-page.change-number",
                    defaultMessage: "Change Phone Number",
                  })}
            </InteractiveContainer>
          </div>
        }
        onSubmit={verifyOtp}
        onResend={onResendOtp}
        error={otpError}
      />
    </div>
  );
};
